import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "Tools, Harnesses and Skills: The Missing Model for Design Systems",
  "slug": "tools-harnesses-and-skills-the-missing-model-for-design-systems",
  "publishedAt": "2026-06-07T08:00:00.000Z",
  "readTime": "9 min read",
  "excerpt": "Most agentic design workflow conversations confuse the workspace, the operating model and the reusable capability. Separating them changes how teams scale.",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "status": "scheduled",
  "series": "Agentic Design Systems",
  "tags": ["Design Systems", "Agentic Workflows", "Figma", "Storybook"],
  "mainImageUrl": "/blog/agentic-design-systems/hero-02-tools-harness-skills.png",
  "mainImageAlt": "Three-layer diagram: tools workspace, harness boundaries, skills playbooks"
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    figcaption: "figcaption",
    figure: "figure",
    h2: "h2",
    hr: "hr",
    li: "li",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ..._provideComponents(),
    ...props.components
  }, {ArticleFigure} = _components;
  if (!ArticleFigure) _missingMdxReference("ArticleFigure", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Most agentic design-system conversations start with model choice. That is rarely where teams lose."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Thesis:"
      }), " Tools, harnesses and skills are three different layers. Tools are where work happens. Harnesses are how work stays safe. Skills are what valuable work becomes repeatable. When you treat them as one purchase decision (“we need Claude in Figma”) you get impressive demos and unreviewable diffs. When you separate them, the same ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "operating model"
      }), " survives tool churn."]
    }), "\n", _jsxs(_components.p, {
      children: ["When I ", _jsx(_components.a, {
        href: "/blog/figma-mcp-design-systems",
        children: "connected Figma’s MCP server to a live design system"
      }), ", the interesting part was not the model but the metadata: intent, variant logic, token semantics where the tool could read them. Swap the IDE but keep the operating model: the workflow still works. Swap the structure but keep the IDE: you get impressive demos followed by unreviewable diffs."]
    }), "\n", _jsxs(_components.p, {
      children: ["Mature teams separate three things that are usually conflated: ", _jsx(_components.strong, {
        children: "tools"
      }), " (where work happens), ", _jsx(_components.strong, {
        children: "harnesses"
      }), " (how work stays safe) and ", _jsx(_components.strong, {
        children: "skills"
      }), " (what valuable work becomes repeatable). The map is three layers, not three buzzwords; the sections below are how to use it without buying another chat client."]
    }), "\n", _jsxs(ArticleFigure, {
      layout: "full",
      children: [_jsx("img", {
        src: "/blog/agentic-design-systems/fig-tools-layers.webp",
        alt: "Three layers: tools workspace, harness boundaries and skill playbooks."
      }), _jsx("figcaption", {
        children: "Tools expose surfaces; harnesses enforce boundaries; skills package repeatable work with evidence."
      })]
    }), "\n", _jsx(_components.h2, {
      children: "Tools are the workspace"
    }), "\n", _jsx(_components.p, {
      children: "Tools expose surfaces: files, components, diffs, previews, logs, CI. Figma, Storybook, GitHub, Cursor, Claude Code and MCP integrations all live here. Good tools reduce context switching; bad tools hide state until something breaks in CI. None of them create discipline by themselves. Storybook does not guarantee maturity; Figma variables do not guarantee token discipline. The tool gives the system a place to act; the harness defines how action is allowed to proceed."
    }), "\n", _jsx(_components.p, {
      children: "When teams say “connect AI to Figma,” they usually mean one of three shapes, all tools, none a substitute for contracts or CI."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.figma.com/blog/introducing-figma-mcp-server/",
        children: "Figma’s official MCP server"
      }), " (the bridge we wrote up in ", _jsx(_components.a, {
        href: "/blog/figma-mcp-design-systems",
        children: "Figma MCP"
      }), ") routes file context and component metadata into the agent loop. Use it when the agent needs to read component structure, variant properties and file organization without leaving the IDE. It is the default starting point if you already live in Figma Dev Mode."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://github.com/southleft/figma-console-mcp",
        children: "Figma Console MCP"
      }), " by TJ Pitre goes further on extraction, creation and debugging; see ", _jsx(_components.a, {
        href: "https://docs.figma-console-mcp.southleft.com/",
        children: "docs.figma-console-mcp.southleft.com"
      }), ". Reach for it when you need deeper programmatic access: batch extraction, creation workflows or debugging plugin-level issues that the official server does not expose."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://github.com/silships/figma-cli",
        children: "figma-cli"
      }), " by Sil Bormüller is deliberately not MCP: a CLI that talks to Figma Desktop for batch work such as importing a ", _jsx(_components.code, {
        children: "DESIGN.md"
      }), " into variables (", _jsx(_components.a, {
        href: "https://www.intodesignsystems.com/blog/claude-code-figma-no-mcp",
        children: "walkthrough"
      }), "). Use it when the job is migration or bulk token setup, not interactive pair programming in the editor."]
    }), "\n", _jsxs(_components.p, {
      children: ["Pick the surface that matches the job, then wire it into the same harness regardless of transport. The mistake is assuming the transport ", _jsx(_components.em, {
        children: "is"
      }), " the system. MCP, CLI and REST are pipes. The harness decides what may flow through them."]
    }), "\n", _jsx(_components.h2, {
      children: "Harnesses are the operating model"
    }), "\n", _jsx(_components.p, {
      children: "A harness answers what agents will not infer: which files are authoritative, what must be read before editing, which paths are out of scope, which commands prove safety and when a human must review judgment rather than mechanics. Without it, teams collect agent wins and production surprises in equal measure."
    }), "\n", _jsxs(_components.p, {
      children: ["We do not ship a single ", _jsx(_components.code, {
        children: "agent-harness.yml"
      }), ". The harness is distributed: ", _jsx(_components.code, {
        children: "AGENTS.md"
      }), ", ", _jsx(_components.code, {
        children: "docs/LLM_COMPONENT_GENERATION_RULES.md"
      }), ", per-component ", _jsx(_components.code, {
        children: ".contract.json"
      }), " files, verification scripts. That is intentional. A monolithic harness file rots the day someone adds a new package root. Distributed harness pieces version with the code they protect."]
    }), "\n", _jsx(_components.p, {
      children: "The YAML below is illustrative: the shape those pieces take when you compile them for one component edit."
    }), "\n", _jsxs(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: [_jsx(_components.figcaption, {
        "data-rehype-pretty-code-title": "",
        "data-language": "yaml",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: "agent-harness.example.yml"
      }), _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "yaml",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "yaml",
          "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#6A737D",
                "--shiki-dark": "#6A737D",
                "--shiki-hcw": "#66707B",
                "--shiki-hcb": "#BDC4CC"
              },
              children: "# Illustrative, not a file in our repo"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "scope"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  allowed_paths"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "nextjs-app/shared/components/Button/**"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  blocked_paths"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "nextjs-app/shared/styles/variables.css"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "required_context"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "docs/LLM_COMPONENT_GENERATION_RULES.md"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "Button.contract.json"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "Button.stories.tsx"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "verification"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "npm run typecheck"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "npm run validate:components -- --component Button"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "npm run test -- Button"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "human_review"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#22863A",
                "--shiki-dark": "#85E89D",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  required_when"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ":"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "variant taxonomy changes"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "    - "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "accessibility exception requested"
            })]
          })]
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "Notice what is missing: model names, prompt poetry, brand adjectives. A harness is operational, not inspirational. It should fit on a screen and fail loudly when ignored."
    }), "\n", _jsxs(_components.p, {
      children: ["Good harness properties are boring. ", _jsx(_components.strong, {
        children: "Scope"
      }), " prevents drive-by refactors in token files. ", _jsx(_components.strong, {
        children: "Required context"
      }), " stops agents from inventing components from memory. ", _jsx(_components.strong, {
        children: "Verification"
      }), " turns “looks fine” into commands. ", _jsx(_components.strong, {
        children: "Human review triggers"
      }), " reserve judgment for changes that lint cannot encode: taxonomy shifts, accessibility exceptions, copy that carries legal weight."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://projectspine.dev",
        children: "Project Spine"
      }), " packages a similar idea: curated context bundles instead of the whole monorepo dumped into a prompt. Whether you use Spine, ", _jsx(_components.code, {
        children: "AGENTS.md"
      }), " or a compiled YAML file, the test is the same: can a new agent session find the authoritative answer without reading fifty unrelated files?"]
    }), "\n", _jsx(_components.h2, {
      children: "Skills are repeatable capabilities"
    }), "\n", _jsx(_components.p, {
      children: "Skills are named playbooks inside the harness: audit token drift, generate a story matrix from a contract, compare Figma variants to React props, promote a component with checklist evidence. If a task happens every sprint, it should not depend on whoever is online that day."
    }), "\n", _jsxs(_components.p, {
      children: ["A skill is not a clever prompt. It is a procedure with inputs, steps and exit criteria. “Write good stories” is not a skill. “Given ", _jsx(_components.code, {
        children: "Button.contract.json"
      }), ", ensure required stories exist, add keyboard coverage for loading state, run axe, list gaps honestly” is closer."]
    }), "\n", _jsxs(_components.p, {
      children: ["On ", _jsx(_components.a, {
        href: "https://www.digitaltableteur.com",
        children: "digitaltableteur.com"
      }), " we publish ", _jsx(_components.code, {
        children: "dt-*"
      }), " skills as ", _jsx(_components.code, {
        children: "SKILL.md"
      }), " playbooks, indexed at ", _jsx(_components.a, {
        href: "https://www.digitaltableteur.com/.well-known/agent-skills/index.json",
        children: "/.well-known/agent-skills/"
      }), ". We are moving toward JSON skill contracts; today the source of truth is markdown with front matter. Target shape:"]
    }), "\n", _jsxs(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: [_jsx(_components.figcaption, {
        "data-rehype-pretty-code-title": "",
        "data-language": "json",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: "skill.contract.target.json"
      }), _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "json",
        "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "json",
          "data-theme": "github-light github-dark github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"name\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"component-a11y-pass\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"input\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"componentName\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"contractPath\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"storyPath\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "],"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#005CC5",
                "--shiki-dark": "#79B8FF",
                "--shiki-hcw": "#024C1A",
                "--shiki-hcb": "#72F088"
              },
              children: "  \"exitCriteria\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ": ["
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "    \"axe expectations documented\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "    \"keyboard behavior covered\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#032F62",
                "--shiki-dark": "#9ECBFF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "    \"manual AT gaps listed honestly\""
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "  ]"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#24292E",
                "--shiki-dark": "#E1E4E8",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Three skills cover most early agentic DS work before you need a library of dozens. ", _jsx(_components.strong, {
        children: "Promotion skill:"
      }), " given a component, verify contract, stories, axe and migration notes before status moves from beta to stable. ", _jsx(_components.strong, {
        children: "Drift audit skill:"
      }), " compare Figma variant names to CVA props and file a diff report. ", _jsx(_components.strong, {
        children: "Pattern pick skill:"
      }), " given a user story, return the pattern recipe and forbidden combinations before any code is written. Skills compose: the pattern pick skill should refuse to proceed if the harness files are missing."]
    }), "\n", _jsx(_components.h2, {
      children: "Diagnose before you buy another tool"
    }), "\n", _jsx(_components.p, {
      children: "When layers blur, teams fix the wrong problem. Inconsistent UI is often weak contracts, not a weak model. The wrong button variant is often missing action hierarchy in the contract, not a bad prompt. Slow review is often missing mechanical checks, not missing approvers."
    }), "\n", _jsx(_components.p, {
      children: "Use a simple matrix when something fails:"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Symptom"
          }), _jsx(_components.th, {
            children: "Likely layer"
          }), _jsx(_components.th, {
            children: "Fix"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Agent edits files outside the component"
          }), _jsx(_components.td, {
            children: "Harness scope"
          }), _jsx(_components.td, {
            children: "Tighten allowed paths; block token globals"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Agent picks wrong component but valid props"
          }), _jsx(_components.td, {
            children: "Skill or contract gap"
          }), _jsx(_components.td, {
            children: "Pattern pick skill; semantic rules"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Agent copies stale story"
          }), _jsx(_components.td, {
            children: "Context curation"
          }), _jsx(_components.td, {
            children: "Mark legacy stories; update examples"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Valid UI, wrong product judgment"
          }), _jsx(_components.td, {
            children: "Human review trigger"
          }), _jsx(_components.td, {
            children: "Escalate destructive flows"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "CI green, design wrong"
          }), _jsx(_components.td, {
            children: "Evidence gap"
          }), _jsx(_components.td, {
            children: "Add contract rule or required story"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Say the agent picked the wrong button variant. That can be a ", _jsx(_components.a, {
        href: "/blog/component-contracts-are-where-taste-becomes-infrastructure",
        children: "contract gap"
      }), ", a missing skill (“choose action pattern for this context”) or a harness gap (validation never ran before handoff). It is rarely solved by switching IDEs."]
    }), "\n", _jsx(_components.p, {
      children: "Before adding tooling, name what each column does for your squad. Tools make work visible. Harnesses define boundaries. Skills encode repeatability. The tool stack can change weekly; harness clarity is what agents actually inherit."
    }), "\n", _jsx(_components.h2, {
      children: "Tool churn without harness churn"
    }), "\n", _jsx(_components.p, {
      children: "Teams rotate IDEs, models and MCP servers faster than they update docs. That is fine if the harness is stable. When Figma MCP replaced a manual export script in one squad I advised, the harness files (contracts, required context, verification commands) stayed the same. Only the tool column changed. When another squad swapped harness clarity instead, swapping from Claude Code to Cursor did not help: agents still edited token files because scope was never written down."
    }), "\n", _jsxs(_components.p, {
      children: ["Document harness changes in the same PR as tool changes. If you add a new MCP server, add the allowed paths it may touch. If you remove a server, remove stale references from ", _jsx(_components.code, {
        children: "AGENTS.md"
      }), " so agents do not hunt missing tools."]
    }), "\n", _jsx(_components.h2, {
      children: "A minimal harness audit"
    }), "\n", _jsx(_components.p, {
      children: "Run this checklist before claiming agentic readiness:"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "Can you name the authoritative doc for component creation in one link?"
      }), "\n", _jsx(_components.li, {
        children: "Does every production component have a contract or an explicit exception logged?"
      }), "\n", _jsxs(_components.li, {
        children: ["Does CI run at least one design-system validator on every PR touching ", _jsx(_components.code, {
          children: "shared/components"
        }), "?"]
      }), "\n", _jsx(_components.li, {
        children: "Are legacy stories marked or removed from default examples?"
      }), "\n", _jsx(_components.li, {
        children: "Is there a human review trigger for taxonomy and accessibility exceptions?"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Few teams pass all five. Passing three is a honest start. Failing all five while running agents in production is how you get “AI made us slower.”"
    }), "\n", _jsx(_components.h2, {
      children: "Storybook as agent training data"
    }), "\n", _jsx(_components.p, {
      children: "Storybook is not a deliverable for stakeholders. For agentic workflows, it is training data. Agents do not infer your intent from principles; they mimic examples. Curate defaults the way you curate public API: the Default story should be the product-correct baseline, not the prettiest marketing layout."
    }), "\n", _jsxs(_components.p, {
      children: ["Rename stories that mislead. If ", _jsx(_components.code, {
        children: "Example"
      }), " shows deprecated padding, delete or fix it before an agent treats it as canonical. Add ", _jsx(_components.code, {
        children: "Legacy"
      }), " or ", _jsx(_components.code, {
        children: "Deprecated"
      }), " to titles when you must keep history. Link from the story docs tab to the contract status field. Context operators (Part 5) own this curation weekly, not once a quarter."]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Series:"
      }), " Part 2 of 5, ", _jsx(_components.strong, {
        children: "←"
      }), " ", _jsx(_components.a, {
        href: "/blog/agentic-design-systems-need-operating-models-not-more-components",
        children: "Operating Models"
      }), ", ", _jsx(_components.strong, {
        children: "→"
      }), " ", _jsx(_components.a, {
        href: "/blog/component-contracts-are-where-taste-becomes-infrastructure",
        children: "Component Contracts"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
